import React, { useCallback, useEffect, useState } from 'react';

// "Criar OC pela nota" (André, 30/10/2026): roda, só para este card, a MESMA criação
// automática de OC do robô (varrer_criacao_oc no server.py). Primeiro uma prévia que não
// grava nada; o botão "Criar OC" cria de verdade (com as mesmas travas contra OC dupla) e o
// card anda para a etapa seguinte, como na criação automática.

interface Props {
  flowId?: string;
  cardId: string;
  onCriada?: (numeroOc: string) => void;
}

const brl = (v: any) => {
  const n = Number(v);
  return isNaN(n) ? '-' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CriarOcPelaNota: React.FC<Props> = ({ flowId, cardId, onCriada }) => {
  const [carregando, setCarregando] = useState(true);
  const [previa, setPrevia] = useState<any>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);

  const chamar = useCallback(async (ehPrevia: boolean) => {
    const r = await fetch('/api/gix/criacao-oc/varrer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flowId, cardId, previa: ehPrevia }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.success) throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
    const meus = (j.itens || []).filter((i: any) => String(i.cardId) === String(cardId));
    return meus[meus.length - 1] || null;
  }, [flowId, cardId]);

  const carregarPrevia = useCallback(async () => {
    setCarregando(true); setErro(null); setPrevia(null); setResultado(null);
    try {
      const p = await chamar(true);
      if (!p) {
        setErro('Este card não está na etapa "Ordem de compra" nem em "OC automática não criada" — a OC pela nota só sai dessas etapas.');
      } else {
        setPrevia(p);
      }
    } catch (e: any) {
      setErro(e?.message || String(e));
    } finally {
      setCarregando(false);
    }
  }, [chamar]);

  useEffect(() => {
    if (!flowId) { setCarregando(false); setErro('Fluxo do card não identificado.'); return; }
    carregarPrevia();
  }, [flowId, carregarPrevia]);

  const criar = async () => {
    setCriando(true); setErro(null);
    try {
      const r = await chamar(false);
      setResultado(r || { status: 'erro', motivo: 'o servidor não devolveu resultado para este card' });
      if (r?.status === 'criada' && r?.numeroOc) onCriada?.(String(r.numeroOc));
    } catch (e: any) {
      setErro(e?.message || String(e));
    } finally {
      setCriando(false);
    }
  };

  if (carregando) {
    return <div className="text-xs text-gray-600 p-3">Lendo a nota no GIX...</div>;
  }
  if (erro) {
    return <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3">{erro}</div>;
  }
  if (resultado) {
    const ok = resultado.status === 'criada';
    return (
      <div className={`text-sm rounded p-3 border ${ok ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-red-50 border-red-200 text-red-800'}`}>
        <div className="font-semibold mb-1">{ok ? `OC ${resultado.numeroOc} criada no GIX` : 'A OC não foi criada'}</div>
        <div className="text-xs">{resultado.motivo}</div>
        {ok && <div className="text-xs mt-1">O número foi gravado no card e o card seguiu para a próxima etapa.</div>}
      </div>
    );
  }
  if (!previa) return null;

  if (previa.status !== 'pronta') {
    return (
      <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded p-3 space-y-2">
        <div className="font-semibold text-sm">Ainda não dá para criar a OC pela nota</div>
        <div>{previa.motivo}</div>
        {previa.emitente?.cnpj && (
          <div className="text-[11px] text-amber-800">
            Emitente da nota: <b>{previa.emitente.nome || '-'}</b> — CNPJ {previa.emitente.cnpj}
          </div>
        )}
        <button type="button" onClick={carregarPrevia}
          className="px-3 py-1 bg-white border border-amber-400 rounded text-xs hover:bg-amber-100">
          Conferir de novo
        </button>
      </div>
    );
  }

  const ocs: Array<{ nf?: string; payload: any; notaGix?: any }> =
    previa.porNota?.length ? previa.porNota : [{ nf: previa.nf, payload: previa.payload, notaGix: previa.notaGix }];
  const itensProp: any[] = previa.itens || [];

  return (
    <div className="space-y-3 text-xs">
      {ocs.map((o, idx) => {
        const p = o.payload || {};
        const n = o.notaGix || {};
        const itensDaOc = ocs.length === 1 ? itensProp : (p.itens || []);
        const total = (p.itens || []).reduce((s: number, i: any) => s + Number(i.quantidade || 0) * Number(i.preco_unitario || 0), 0);
        return (
          <div key={idx} className="border border-emerald-200 rounded p-3 bg-emerald-50/40 space-y-1">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div><span className="text-gray-500">Empresa:</span> <b>{p.empresa}</b></div>
              <div><span className="text-gray-500">NF:</span> <b>{n.numero || o.nf}</b>{n.emissao ? ` de ${String(n.emissao).slice(0, 10)}` : ''}</div>
              <div className="col-span-2"><span className="text-gray-500">Fornecedor:</span> <b>{n.nome_fornecedor || '-'}</b> ({p.fornecedor})</div>
              <div><span className="text-gray-500">Movimento:</span> {p.tipo_movimento}</div>
              <div><span className="text-gray-500">Condição:</span> {p.condicao_pagamento} · CC {p.centro_custo} · despesa {p.tipo_despesa}</div>
            </div>
            <table className="w-full mt-2 text-[11px]">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1">Item da nota → produto no GIX</th>
                  <th className="py-1 text-right">Qtd</th>
                  <th className="py-1 text-right">Unit.</th>
                  <th className="py-1 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {itensDaOc.map((it: any, k: number) => (
                  <tr key={k} className="border-b border-gray-100">
                    <td className="py-1">
                      {it.descricao_nf ? <>{it.descricao_nf} <span className="text-gray-400">→</span> </> : null}
                      <b>{it.produto_desc || it.produto}</b>{it.produto ? <span className="text-gray-400"> #{it.produto}</span> : null}
                    </td>
                    <td className="py-1 text-right">{it.quantidade}</td>
                    <td className="py-1 text-right">{brl(it.preco_unitario)}</td>
                    <td className="py-1 text-right">{brl(it.valor_total ?? Number(it.quantidade || 0) * Number(it.preco_unitario || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right font-semibold text-sm pt-1">Total da OC: {brl(total)}</div>
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        <button type="button" onClick={criar} disabled={criando}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-semibold disabled:opacity-50">
          {criando ? 'Criando...' : (ocs.length > 1 ? `Criar ${ocs.length} OCs` : 'Criar OC')}
        </button>
        <span className="text-[11px] text-gray-500">Cria no GIX de verdade, grava o número no card e o card segue para a próxima etapa.</span>
      </div>
    </div>
  );
};

export default CriarOcPelaNota;
